import { useEffect, useState } from 'react';
import { useChatStore } from '../stores/chat';
import { useTasksStore } from '../stores/tasks';
// useChatStore/useTasksStore.getState() used in handleNotificationNavigate
import { useAuthStore } from '../stores/auth';
import { useNotificationsStore } from '../stores/notifications';
import { getSocket } from '../lib/socket';
import type { Task } from '../lib/api';
import type { AppNotification } from '../stores/notifications';
import TopBar from '../components/layout/TopBar';
import Sidebar from '../components/layout/Sidebar';
import ChatPanel from '../components/chat/ChatPanel';
import TasksPanel from '../components/tasks/TasksPanel';
import TaskDetailScreen from '../components/tasks/TaskDetailScreen';
import ContentWrapper from '../components/layout/ContentWrapper';
import InstallPrompt from '../components/pwa/InstallPrompt';
import { useMobile } from '../hooks/useMobile';
import { useNotificationSounds } from '../hooks/useNotificationSounds';

export type SubTab = 'clients' | 'internal' | 'tasks';

export default function AppPage() {
  const [subTab, setSubTab]         = useState<SubTab>('clients');
  const [mobileView, setMobileView] = useState<'sidebar' | 'content'>('sidebar');
  const isMobile = useMobile();

  const loadThreads     = useChatStore((s) => s.loadThreads);
  const loadUsers       = useChatStore((s) => s.loadUsers);
  const loadWorkspaceSettings = useChatStore((s) => s.loadWorkspaceSettings);
  const setupSocket     = useChatStore((s) => s.setupSocketListeners);
  const selectThread    = useChatStore((s) => s.selectThread);
  const startDm         = useChatStore((s) => s.startDm);
  const createGroup     = useChatStore((s) => s.createGroup);
  const activeThreadId  = useChatStore((s) => s.activeThreadId);
  const loadTasks          = useTasksStore((s) => s.loadTasks);
  const setupTaskSocket    = useTasksStore((s) => s.setupSocketListeners);
  const setTaskFilter      = useTasksStore((s) => s.setFilter);
  const currentUser        = useAuthStore((s) => s.user);
  const logout             = useAuthStore((s) => s.logout);
  const setupNotifSocket   = useNotificationsStore((s) => s.setupSocketListeners);
  const requestNotifPerm   = useNotificationsStore((s) => s.requestPermission);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  useNotificationSounds();

  useEffect(() => {
    loadThreads();
    loadUsers();
    loadWorkspaceSettings();
    setupSocket();
    loadTasks();
    setupTaskSocket();
    setupNotifSocket();
    requestNotifPerm();

    const sock = getSocket();
    const handleExpiry = () => { logout(); window.location.href = '/login'; };
    sock.on('session:expired', handleExpiry);
    return () => { sock.off('session:expired', handleExpiry); };
  }, []);

  // When the tab returns to the foreground or the network comes back, force a
  // reconnect (idle tabs get throttled and silently dropped) and reconcile any
  // events missed while away. If already connected, resync directly.
  useEffect(() => {
    function reconnectAndSync() {
      const sock = getSocket();
      if (sock.disconnected) {
        sock.connect(); // the socket's 'connect' handler triggers resync
      } else {
        void useChatStore.getState().resync();
      }
    }
    const onVisible = () => { if (document.visibilityState === 'visible') reconnectAndSync(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', reconnectAndSync);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', reconnectAndSync);
    };
  }, []);

  // On mobile, auto-navigate to content when a thread is selected
  useEffect(() => {
    if (isMobile && activeThreadId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMobileView('content');
    }
  }, [activeThreadId, isMobile]);

  function handleSelectThread(id: string) {
    selectThread(id);
    if (isMobile) setMobileView('content');
  }

  async function handleStartDm(userId: string) {
    const { data } = await startDm(userId);
    if (!data?.id) throw new Error('No thread ID returned');
    await selectThread(data.id);
    setSubTab('internal');
    if (isMobile) setMobileView('content');
  }

  async function handleCreateGroup(name: string, participantIds: string[]) {
    const { data } = await createGroup(name, participantIds);
    if (!data?.id) throw new Error('No thread ID returned');
    await selectThread(data.id);
    setSubTab('internal');
    if (isMobile) setMobileView('content');
  }

  function handleSubTabChange(t: SubTab) {
    setSubTab(t);
  }

  function handleSelectTask(task: Task) {
    setTaskFilter(task.assigned_to === currentUser?.id ? 'mine' : 'all');
    setSelectedTaskId(task.id);
    setSubTab('tasks');
    if (isMobile) setMobileView('content');
  }

  function handleBack() {
    setSelectedTaskId(null);
    setMobileView('sidebar');
  }

  function handleNotificationNavigate(notif: AppNotification) {
    if (notif.threadId) {
      selectThread(notif.threadId);
      // Switch to the right subtab
      const threads = useChatStore.getState().threads;
      const thread = threads.find((t) => t.id === notif.threadId);
      if (thread) {
        setSubTab(thread.type === 'client' ? 'clients' : 'internal');
      }
      if (isMobile) setMobileView('content');
    } else if (notif.taskId) {
      const task = useTasksStore.getState().tasks.find((t) => t.id === notif.taskId);
      if (task) handleSelectTask(task);
    }
  }

  const showSidebar = !isMobile || mobileView === 'sidebar';
  const showContent = !isMobile || mobileView === 'content';

  // Mobile: tasks are listed in the sidebar and open fullscreen; the board is desktop-only
  const contentPanel = subTab === 'tasks' && isMobile && selectedTaskId ? (
    <TaskDetailScreen taskId={selectedTaskId} onBack={handleBack} />
  ) : subTab === 'tasks' ? (
    <ContentWrapper title="Tasks" onBack={isMobile ? handleBack : undefined}>
      <TasksPanel selectedTaskId={selectedTaskId} onTaskViewed={() => setSelectedTaskId(null)} />
    </ContentWrapper>
  ) : (
    <ChatPanel onBack={isMobile ? handleBack : undefined} />
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {(!isMobile || mobileView === 'sidebar') && <TopBar onNotificationNavigate={handleNotificationNavigate} />}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {showSidebar && (
          <Sidebar
            subTab={subTab}
            onSubTabChange={handleSubTabChange}
            onSelectThread={handleSelectThread}
            onSelectTask={handleSelectTask}
            onStartDm={handleStartDm}
            onCreateGroup={handleCreateGroup}
            style={isMobile ? { width: '100%' } : undefined}
          />
        )}

        {showContent && (
          <div style={{ flex: 1, background: 'var(--vs1)', padding: '10px 8px', display: 'flex', overflow: 'hidden' }}>
            <div style={{
              flex: 1, borderRadius: 8, border: '1px solid var(--shell-border)',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              background: 'var(--surface-app)',
            }}>
              {contentPanel}
            </div>
          </div>
        )}
      </div>

      <InstallPrompt />
    </div>
  );
}
